import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { BrandBadge } from "@/components/BrandBadge";
import { ApplyDialog } from "@/components/ApplyDialog";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { timeAgo } from "@/lib/utils.jobs";
import { toast } from "sonner";
import {
  MapPin, CurrencyGbp, CurrencyDollar, Briefcase, Clock, Bookmark, ArrowLeft, PaperPlaneTilt, ShieldCheck,
} from "@phosphor-icons/react";

export default function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/jobs/${id}`).then(({ data }) => setJob(data)).catch(() => setJob(null)).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (user && user !== false) {
      api.get("/me/saved-jobs").then(({ data }) => setSaved(data.saved_ids.includes(id))).catch(() => {});
    }
  }, [user, id]);

  const toggleSave = async () => {
    if (!user || user === false) return toast.error("Please sign in to save jobs");
    try {
      if (saved) { await api.delete(`/me/saved-jobs/${id}`); toast.success("Removed from saved"); }
      else { await api.post(`/me/saved-jobs/${id}`); toast.success("Job saved"); }
      setSaved(!saved);
    } catch { toast.error("Something went wrong"); }
  };

  if (loading) {
    return (
      <div className="App bg-secondary/40">
        <Navbar />
        <div className="mx-auto max-w-4xl px-4 py-10 md:px-8">
          <Skeleton className="h-64 w-full rounded-2xl" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="App">
        <Navbar />
        <div className="mx-auto max-w-4xl px-4 py-24 text-center md:px-8">
          <h1 className="font-heading text-2xl font-bold">Job not found</h1>
          <p className="mt-2 text-muted-foreground">This vacancy may have been filled or removed.</p>
          <Link to="/jobs"><Button className="mt-6 rounded-full">Browse all jobs</Button></Link>
        </div>
        <Footer />
      </div>
    );
  }

  const Currency = job.country === "US" ? CurrencyDollar : CurrencyGbp;
  const meta = [
    job.location && { icon: MapPin, text: job.location },
    job.salary && { icon: Currency, text: job.salary },
    job.job_type && { icon: Briefcase, text: job.job_type },
    { icon: Clock, text: `Posted ${timeAgo(job.posted_at)}` },
  ].filter(Boolean);

  const looksLikeHtml = /<[a-z][\s\S]*>/i.test(job.description || "");

  const applyNow = () => {
    if (job.apply_url) {
      window.open(job.apply_url, "_blank", "noopener,noreferrer");
    } else {
      setApplyOpen(true);
    }
  };

  return (
    <div className="App bg-secondary/40">
      <Navbar />

      <div className="mx-auto max-w-4xl px-4 py-8 md:px-8">
        <Link to="/jobs" className="mb-5 inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-primary" data-testid="back-to-jobs">
          <ArrowLeft size={16} /> Back to jobs
        </Link>

        <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
          <div className="flex items-start gap-4">
            <BrandBadge size={56} />
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight md:text-3xl" data-testid="job-detail-title">{job.title}</h1>
              <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                <ShieldCheck size={16} weight="fill" className="text-accent" /> Managed by The Vacancy Network
              </p>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-foreground/80">
            {meta.map((m, i) => (
              <span key={i} className="flex items-center gap-1.5">
                <m.icon size={16} weight="fill" className="text-accent" /> {m.text}
              </span>
            ))}
          </div>

          {job.category && (
            <span className="mt-4 inline-block rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
              {job.category}
            </span>
          )}

          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Button className="rounded-full px-8" onClick={applyNow} data-testid="apply-job-btn">
              <PaperPlaneTilt size={18} weight="fill" className="mr-1" /> Apply Now
            </Button>
            <Button variant="outline" className="rounded-full" onClick={toggleSave} data-testid="save-job-detail-btn">
              <Bookmark size={18} weight={saved ? "fill" : "regular"} className="mr-1" />
              {saved ? "Saved" : "Save Job"}
            </Button>
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-border bg-card p-6 md:p-8">
          <h2 className="mb-4 font-heading text-lg font-bold">Job description</h2>
          {looksLikeHtml ? (
            <div
              className="prose prose-sm max-w-none text-sm leading-relaxed text-foreground/85 prose-headings:font-heading prose-a:text-primary"
              data-testid="job-description"
              dangerouslySetInnerHTML={{ __html: job.description }}
            />
          ) : (
            <div className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/85" data-testid="job-description">
              {job.description || "No description provided for this role."}
            </div>
          )}

          <div className="mt-8 rounded-xl bg-secondary/60 p-4 text-sm text-muted-foreground">
            <ShieldCheck size={18} weight="fill" className="mb-1 inline text-accent" /> Managed by The Vacancy Network. Click Apply Now to continue to the application for this role.
          </div>
        </div>
      </div>

      <ApplyDialog open={applyOpen} onOpenChange={setApplyOpen} job={job} />
      <Footer />
    </div>
  );
}
